import React, { useCallback } from 'react';
import { useState, useEffect, useFormContext, Hooks, Code, Form, Field, Name, Title, Actions, Toast, API, Utility, Button, Files, Control, OptionType } from 'helpers/FormImports';
import { Messages } from 'components/common/MessageManager';
import { Referral } from 'models/ReferralModel';
import HtmlEditor from 'components/common/HtmlEditor';
import { CommonDataColumn } from 'models/BaseModel';
import { Template } from 'models/TemplateModel';
import { Exam } from 'models/ExamModel';
import { Location } from 'models/LocationModel';
import { Doctor } from 'models/DoctorModel';
import { RecDoctor } from 'models/RecDoctorModel';
import AsyncSelect from 'react-select/async';
import { Client } from 'models/ClientModel';
import { City } from 'models/CityModel';
import { useSearchParams } from 'react-router-dom';


const ReferralForm: React.FC = () => {
  const [formId, formName] = [Code.Form.Id.REFERRAL, Code.Form.Name.REFERRAL];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [doctorOptions, setDoctorOptions] = Hooks.Form.Options();
  const [recDoctorOptions, setRecDoctorOptions] = Hooks.Form.Options();
  const [recDoctorList, setRecDoctorList] = Hooks.Form.Options();
  const [templateOptions, setTemplateOptions] = Hooks.Form.Options();
  const [examOptions, setExamOptions] = Hooks.Form.Options();
  const [branchOptions, setBranchOptions] = Hooks.Form.Options();
  const [formData, setFormData] = useState<Referral>({});
  const [referralData, setReferralData] = useState<string | undefined>(undefined);
  const [rawReferralData, setRawReferralData] = useState<string>('');
  const [selectedRecDoctors, setSelectedRecDoctors] = useState<string[]>([]);
  const [examList, setExamList] = useState<Exam[]>([]);
 
  const [templateData, setTemplateData] = useState<Template>({});
  const [clientData, setClientData] = useState<Client>({});
  const [cityData, setCityData] = useState<City>({});
  const [examData, setExamData] = useState<Exam>({});
  const [branchData, setBranchData] = useState<Location>({});
  const [doctorData, setDoctorData] = useState<Doctor>({});
  const [recDoctorData, setRecDoctorData] = useState<RecDoctor>({});
  const [examDataColumn, setCommonDataColumn] = useState<CommonDataColumn>({});

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get(Code.UrlPara.Client);
    
  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
  
  useEffect(() => {
    Files.Json.API(Code.File.DOCTOR, Code.Option.Common.SELECT, setDoctorOptions, setError);
    Files.Json.API(Code.File.RECDOCTOR, Code.Option.Common.SELECT, setRecDoctorOptions, setError);
    Files.Json.API(Code.File.RECDOCTOR, Code.Option.Common.NOTHING, setRecDoctorList, setError);
    Files.Json.API(Code.File.TEMPLATE, Code.Option.Common.SELECT, setTemplateOptions, setError);
    Files.Json.API(Code.File.LOCATION, Code.Option.Common.SELECT, setBranchOptions, setError);
  
    API.Data.GetByKeyId<Referral>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
        setActiveId(data.referral_active); 
        const clientData = await API.Data.getClientNameNumber(data.client_id);
        const formData = await Form.Map.Data(data);
        setReferralData(data.referral_data);
        setSelectedRecDoctors(JSON.parse(data.recdoctor_list!));
        setFormData((prevData) => ({
          ...prevData,
          ...formData,
          ...clientData,
        }));
        setLoading(false);
      }})

      const onNewEntry = async () => {
        if(keyId===Code.Key.NEW && clientId) {
          const clientName = await API.Data.GetClientName(Number(clientId));
          setFormData((prevData) => ({
            ...prevData,
            client_id: Number(clientId),
            client_name: Utility.Value.Text(clientName), 
          }));
        }
        setLoading(false);
      };
      onNewEntry();

  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;
    const addonData = await API.Data.AddonData(formData, formName, keyId);
    const finalData = Utility.Sanitize.FormData({
      ...formData,
      ...addonData,
      referral_name: Utility.Convert.TitleCase(formData.referral_name),
      recdoctor_list: JSON.stringify(selectedRecDoctors),
      referral_data: referralData,
    });
    API.Data.Save(formName, keyId, finalData, setMessage)
  };

  //------------ onClick fetch template data
  const OnClickFetchTemplate = async () => {
    const fakeEvent = { preventDefault: () => {} } as React.FormEvent;
    if (!Toast.Validate.Form(fakeEvent, setMessage, "generate")) return;

    if (formData) {
      const template_id = formData.template_id;
      if (Utility.isValid.Id(template_id)) {
        callTemplateData(template_id!);
      }
    }
  };

  //------------ get template data by template_id
  const callTemplateData = useCallback(async (template_id: number) => {
    const template = await API.Data.GetByKeyId<Template>(Code.Form.Name.TEMPLATE, template_id.toString(), setLoading, setError);
    if (template) {
     setTemplateData(template);
    }
  }, []);

  //------------ Trigger when templateData changes.
  useEffect(() => {
    if (templateData) {
      const data = templateData.template_data;
      const name = templateData.template_name;

      if(data) { 
        let rawData = data;
        const today = Utility.Date.Current();
        rawData = rawData.replaceAll("{{current_date}}", Utility.Format.Date(today));
        rawData = rawData.replaceAll("{{current_time}}", Utility.Format.Date(today, Code.Format.DateTime.TimeTT));
        setRawReferralData(rawData); 
      }
      if(name) { setFormData((prev) => ({ ...prev, referral_name: name })); }

      const AllDataReady = clientData && doctorData && recDoctorData && examData && examDataColumn;

      const runSequential = async () => {
    
        let rawData = rawReferralData;

        rawData = await updateRawReferralData(rawData, clientData);
        rawData = await updateRawReferralData(rawData, doctorData);
        rawData = await updateRawReferralData(rawData, recDoctorData);
        rawData = await updateRawReferralData(rawData, branchData);
        rawData = await updateExamColumnData(rawData, examDataColumn);
      
        if (rawData !== rawReferralData) {
          setRawReferralData(rawData);
        }
      };

      if(AllDataReady) {
        console.log("📍all data ready📍"); 
        console.log("📍clientData:",clientData); 
        console.log("📍doctorData:",doctorData); 
        console.log("📍recDoctorData:",recDoctorData); 
        console.log("📍branchData:",branchData); 
        console.log("📍examData:",examData); 
        console.log("📍examDataColumn:",examDataColumn); 
        runSequential();   
      }

    }
  }, [templateData]);

  useEffect(() => {
    const fetchClient = async () => {
      if(formData.client_id){
        const client = await API.Data.GetByKeyId<Client>(Code.Form.Name.CLIENT, formData.client_id, setLoading, setError);
        if (client) { 
          setClientData(client);  
        }
      }
    };
    fetchClient();
  }, [formData.client_id]);

  useEffect(() => {
    const fetchDoctor = async () => {
      if(formData.doctor_id){
        const doctor = await API.Data.GetByKeyId<Doctor>(Code.Form.Name.DOCTOR, formData.doctor_id, setLoading, setError);
        if (doctor) { 
          setDoctorData(doctor);  
        }
      }
    };
    fetchDoctor();
  }, [formData.doctor_id]);

  useEffect(() => {
    const fetchRecDoctor = async () => {
      if(formData.recdoctor_id){
        const recDoctor = await API.Data.GetByKeyId<RecDoctor>(Code.Form.Name.RECDOCTOR, formData.recdoctor_id, setLoading, setError);
        if (recDoctor) { 
          setRecDoctorData(recDoctor);  
        }
      }
    };
    fetchRecDoctor();
  }, [formData.recdoctor_id]);

  useEffect(() => {
    const fetchBranch = async () => {
      if(formData.branch_id){
        const branch = await API.Data.GetByKeyId<Location>(Code.Form.Name.LOCATION, formData.branch_id, setLoading, setError);
        if (branch) { 
          setBranchData(branch);  
        }
      }
    };
    fetchBranch();
  }, [formData.branch_id]);

  useEffect(() => {
    const fetchExam = async () => {
      if(formData.exam_id){
        const exam = await API.Data.GetByKeyId<Exam>(Code.Form.Name.EXAM, formData.exam_id, setLoading, setError);
        if (exam) { 
          setExamData(exam); 
        }
      }
    };
    fetchExam();
  }, [formData.exam_id]);

  //------------ Trigger when examData changes. 
  useEffect(() => {
    if (examData) {
      const data = examData.exam_data1;
      if(data) { 
        Files.List.ParseExamData(data, setCommonDataColumn); 
      }
    }
  }, [examData]);

  
  //------------ Trigger when clientData changes. 
  useEffect(() => {
    const runUpdates = async () => {
      const city_id = clientData?.city_id;
      if(city_id)  {
        const city = await API.Data.GetByKeyId<City>(Code.Form.Name.CITY, city_id.toString(), setLoading, setError);
        if(city){
          setCityData(city);
        }
      }
    };
    if (clientData) { runUpdates(); }
  }, [clientData]);
  

  //------------ Update RawReferralData using related data mapping.
  const updateRawReferralData = async (rawData: string, data: Record<string, any>): Promise<string> => {
    if (!data) return rawData;

    if(data && rawData.length){
      Object.entries(data).forEach(([key, value]) => {
        //const placeholder = `{{${Code.Map.TemplateField[key]}}}`;
        const placeholder = `{{${key}}}`;
        console.log("---->",placeholder);
        if (typeof value === 'string' && value.length > 0) {
          if(key.endsWith("_date") || key.endsWith("_dob")) {
            rawData = rawData.replaceAll(placeholder,Utility.Format.Date(value));
          } else {
            rawData = rawData.replaceAll(placeholder,value.replace(/(\r\n|\r|\n)/g, "<br/>"));
          }   
        }
      });
    }
    return rawData;
  };

  const updateExamColumnData = async (rawData: string, data: Record<string, any>): Promise<string> => {
    if (!data) return rawData;
    
    if(data && rawData.length){
      Object.entries(data).forEach(([key, value]) => {
        const placeholder = `{{${key}}}`;
        rawData = rawData.replaceAll(placeholder, value ?? '');
      });
    }
    return rawData;
  };


  //------------ Update final ReferralData when rawReferralData changes.
  useEffect(() => {
    setReferralData(rawReferralData.replace(/'/g, "").replace(/{{[^}]+}}/g, '').replace(/\s{2,}/g, " ")); 
  }, [rawReferralData]); 

  //------------ trigger when client changes.
  useEffect(() => {
    const client_id = formData.client_id;
    console.log("client_id:",client_id);
    if(Utility.isValid.Id(client_id)){
        callExamList(client_id);
    }
  }, [formData.client_id]); 

  //------------ get exam list of selected client.
  const callExamList = async (clientId: number | undefined) => {
    if (!clientId) return;  
    const url = API.Build.FormApiEndpint(Code.Form.Name.EXAM, 0, Name.Exam.Date, Code.Option.Sort.DESC, {
      fields: Utility.Join.Fields([
        Name.Exam.Id,
        Name.Exam.Date, 
      ]),
      [Name.Client.Id]: clientId,
    });
    await API.Data.List<Exam>(url, setExamList, setError, setLoading);
  };

  //------------set exam list to exam dropdown with default value.
  useEffect(() => {
    console.log("examList:",examList);
    if(examList && examList.length){
      const examOptions: OptionType[] = Utility.Convert.AnyToOptions("exam_id", "exam_date", examList, Code.Option.Common.SELECT); 
      if(examOptions && examOptions.length){
          setExamOptions(examOptions);
      }
    }
  }, [examList]); 

  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} formName={formName} formId={formId} Privilege={Privilege} />
          <Control.Tab.Header titles={["Details"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
 
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.FilterSelect id={Name.Doctor.Id} value={formData.doctor_id} title={Title.Doctor} options={doctorOptions} setFormData={setFormData} size={[0,27,1]} require={true} className='generate' />
                <Field.FilterSelect id={Name.RecDoctor.Id} value={formData.recdoctor_id} title={Title.RecDoctor} options={recDoctorOptions} setFormData={setFormData} size={[1,27,1]}  require={true} className='generate' />
                <Field.MultiSelect id={Title.Doctor}  title="CC/Copy to" options={recDoctorList} selectedItems={selectedRecDoctors} setSelectedItems={setSelectedRecDoctors} size={[1,27,1]}/>
                <Field.Date id={Name.Referral.Date} value={formData.referral_date} title={Title.Date} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,19,0]} require={true} currentDate={true} />
              </Control.Row>
              <Control.Row>
                <Field.Header id={Name.Client.Id} title={Title.Client} require={true} className='generate'>
                  <AsyncSelect id={Name.Client.Id} loadOptions={(inputValue) => API.Data.AsyncOption(inputValue, Code.Form.Name.CLIENT, Code.Mapping.client.value, Code.Mapping.client.label)} styles={Code.Style.FormOption} onChange={(selected) => Utility.Handle.ClientChange(selected, setFormData)} value={Utility.Value.Async(formData.client_id,formData.client_name)} placeholder="Search" />
                </Field.Header>
              </Control.Row>
              <Control.Row>
                <Field.FilterSelect id={Name.Template.Id} value={formData.template_id} title={Title.Template} options={templateOptions} setFormData={setFormData} size={[0,54,1]}  require={true} className='generate' />
                <Field.FilterSelect id={Name.Exam.Id} value={formData.exam_id} title={"Exam Date"} options={examOptions} setFormData={setFormData} size={[1,27,1]} require={true} className='generate' />
                <Field.FilterSelect id={Name.Common.BranchId} value={formData.branch_id} title={"Location"} options={branchOptions} setFormData={setFormData} size={[1,19,0]} require={true} className='generate' />
              </Control.Row>
              <Control.Box>
                <Field.Title title="Subject *" size={[2,25,0]}/>
                <Button.Action title="Generate" onClick={OnClickFetchTemplate} className='fright mb2' />
              </Control.Box>
              <Control.Box>
                <Field.Text id={Name.Referral.Name} value={formData.referral_name} onChange={(e) => Form.Field.Change(e, formData, setFormData)} require={true} />
                <HtmlEditor content={referralData} setContent={setReferralData} height={450}/>
              </Control.Box>
              <Control.Box>
                <Field.Area id={Name.Referral.Comment} value={formData.referral_comment} title={Title.Internal} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Box>
            </Control.Tab.Content>
 
          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default ReferralForm;